import type {
  ChainModifiers,
  Entry,
  EntrySkeletonType,
  UnresolvedLink,
} from 'contentful';
import * as moment from 'moment';

import type { TypeActiveElectionFields } from '../../types/contentful.auto/TypeActiveElection';
import type { TypeBallotRequestConfigFields } from '../../types/contentful.auto/TypeBallotRequestConfig';
import type { TypeCustomAlertWithAllLocalesResponse } from '../../types/contentful.auto/TypeCustomAlert';
import type { TypePollingLocationLookupFields } from '../../types/contentful.auto/TypePollingLocationLookup';

import type { ElectionInfo } from '../data/election-types';
import type { State } from '../data/jurisdictions';

import type { ParsedNote } from '../services/contentful-service';

import type {
  ActiveLocale,
  CustomLocale,
  DefaultLocale,
  MinimumEnLocalizedString,
  LocalizedRichTextObj,
  LocalizedString,
  ActiveLocalizedRichTextObj,
  ActiveLocalizedString,
} from '../utils/localization';
import type { Option } from '../utils/option';

/**
 * Type helper to apply to fields that do not have “Enable localization of this
 * field” enabled in Contentful.
 *
 * Reflects that there will always be an `en` property and no other.
 *
 * If the field is optional, this reflects Contentful’s behavior that if the
 * field does have a value, then the `en` property will have a value. (In other
 * words, Contentful would return `{}` rather than `{myField: {en:
 * undefined}}`.)
 *
 * Use the `@localized` directive in the auto-generated type’s JsDoc to let you
 * know if you should use this or `localized`.
 */
export function notLocalized<T>(val: { [k in ActiveLocale]?: T }): T;
export function notLocalized<T>(
  val: { [k in ActiveLocale]?: T } | undefined
): T | undefined;
export function notLocalized<T>(val: { en?: T } | undefined) {
  return val?.en;
}

/**
 * Type helper for localized fields. If the field is required, then it asserts
 * that we’ll have both English and Spanish content (since Spanish has “Allow
 * required fields to be empty for this locale” unchecked in the Contentful
 * configuration).
 *
 * Otherwise passes through… non-required fields are not guaranteed by
 * Contentful to have any content in any language. (For example, they could have
 * Spanish but not English, even though we try to always include English.)
 *
 * Use the `@localized` directive in the auto-generated type’s JsDoc to let you
 * know if you should use this or `notLocalized`.
 */
export function localized<T>(val: { [k in ActiveLocale]?: T }): {
  [k in DefaultLocale]: T;
} & { [k in CustomLocale]?: T };
export function localized<T>(
  val: { [k in ActiveLocale]?: T } | undefined
): { [k in ActiveLocale]?: T } | undefined;
export function localized(val: object | undefined) {
  return val;
}

/**
 * Returns true if the value is a proper {@link Entry}, rather than a
 * {@link contentful.UnresolvedLink}.
 *
 * Used because the types for Contentful allow any of our links to Entries to
 * end up as unresolved, though in practice that doesn’t come up.
 *
 * (One note is that adding .withoutUnresolvableLinks to the Contentful fetch
 * ends up being a step backwards; anything that could be an
 * {@link UnresolvedLink} instead becomes potentially `undefined`, so we lose
 * the ability to distinguish in the types between required and non-required
 * fields. So we don’t use that feature and instead do this type check where
 * necessary.)
 */
export function isEntry<
  E extends EntrySkeletonType,
  M extends ChainModifiers,
  L extends string,
>(
  val: UnresolvedLink<'Entry'> | Entry<E, M, L> | undefined
): val is Entry<E, M, L> {
  return val?.sys.type === 'Entry';
}

export type TerritoryConfig = {
  territoryName: Option<string>;
  territoryCode: Option<string>;
  vepConfig: Option<JurisdictionVepConfig>;
};

export type SitewideAlertsObject = {
  sitewideAlerts: Array<LinkedAlertFields>;
};

export type JurisdictionLocaleSupport = Array<ActiveLocale>;

export type JurisdictionConfig = {
  stateName: string;
  stateCode: string;
  voterHotline: Option<string>;
  hotlineFooterOverride: Option<JurisdictionHotlineFooterOverride>;
  supportedLocales: JurisdictionLocaleSupport;
  priorityLocales: JurisdictionLocaleSupport;
  vepConfig: JurisdictionVepConfig;
  pollingLocationLookupConfig: JurisdictionPollingLookupConfig;
  registrationConfig: Option<JurisdictionRegistrationConfig>;
  jurisdictionAlert: Array<LinkedAlertFields>;
  electionInfo: ElectionInfo | undefined;
  ballotRequestConfig: Option<JurisdictionBallotRequestConfig>;
  updatedAt: string;
  customLandingPageButtons: Option<JurisdictionCustomLandingPageButtons>;
  customPages: JurisdictionCustomPage[];
};

/* 
About `ImportantDates`:
- The only field guaranteed to exist is earlyVotingStartIsSameStatewide.
- While these date types are available, 
  they are not universally relevant across jurisdictions.
- The Contentful model combines the dateBy and dateByNote fields.
- The `ParsedNote` type represents either the (short) Note or Long Note fields,
  as ActiveLocalizedString: (short) Note, or RequiredLocalizedRichText: Long Note.
*/
export type ImportantDates = {
  earlyVotingStartBy: moment.Moment | undefined;
  earlyVotingStartByNote: ParsedNote | undefined;
  earlyVotingStartIsSameStatewide: boolean;
  electionDay: moment.Moment | undefined;
  electionDayStatewideHours: ParsedNote | undefined;
  ballotRequestBy: moment.Moment | undefined;
  ballotRequestByNote: ParsedNote | undefined;
  ballotDropoffBy: moment.Moment | undefined;
  ballotDropoffByNote: ParsedNote | undefined;
  ballotPostmarkBy: moment.Moment | undefined;
  ballotPostmarkByNote: ParsedNote | undefined;
  ballotReceiveBy: moment.Moment | undefined;
  ballotReceiveByNote: ParsedNote | undefined;
  inPersonAbsenteeStartBy: moment.Moment | undefined;
  inPersonAbsenteeStartByNote: ParsedNote | undefined;
  registerOnlineBy: moment.Moment | undefined;
  registerOnlineByNote: ParsedNote | undefined;
  registerInPersonBy: moment.Moment | undefined;
  registerInPersonByNote: ParsedNote | undefined;
  registerSameDayNote: ParsedNote | undefined;
  registerReceiveBy: moment.Moment | undefined;
  registerReceiveByNote: ParsedNote | undefined;
  registerPostmarkBy: moment.Moment | undefined;
  registerPostmarkByNote: ParsedNote | undefined;
  voteByMailNote: ActiveLocalizedRichTextObj | undefined;
};

export type LocationLookupExperienceTypes =
  | TypePollingLocationLookupFields['locationLookupExperience']['values']
  | 'none';

export type JurisdictionPollingLookupConfig = {
  displayVotingLocationLookup: boolean;
  lookupExperience: LocationLookupExperienceTypes;
  sosLookupUrl: MinimumEnLocalizedString | null;
  locateMessage: JurisdictionLocateMessage | null;
  earlyAbsenteeExcuse: {
    copy: ActiveLocalizedRichTextObj | null;
  } | null;
  informationalNotes: {
    dropoffLocations: ActiveLocalizedRichTextObj | null;
    earlyVoteLocations: ActiveLocalizedRichTextObj | null;
    electionDayLocations: ActiveLocalizedRichTextObj | null;
  } | null;
};

export type JurisdictionLocateMessage = {
  headline: ActiveLocalizedString;
  errorMessage: ActiveLocalizedRichTextObj;
};

export type JurisdictionRegistrationConfig = {
  registrationFlowEnabled: boolean;
  displayRegistrationLookup: boolean;
  sameDay: {
    enabled: boolean;
    copy: LocalizedRichTextObj | undefined;
    sameDayHeading: LocalizedString | undefined;
  };
  online: {
    enabled: boolean;
    displayDeadline: boolean;
    copy: LocalizedRichTextObj | undefined;
    onlineUrl: LocalizedString | undefined;
    onlineButtonText: LocalizedString | undefined;
    onlineHeading: ActiveLocalizedString;
    alternateUrl: LocalizedString | undefined;
    alternateButtonText: LocalizedString | undefined;
  };
  mail: {
    enabled: boolean;
    displayDeadline: boolean;
    showRegistrationSteps: boolean;
    copy: LocalizedRichTextObj | undefined;
    mailUrl: LocalizedString | undefined;
    mailButtonText: LocalizedString | undefined;
    mailHeading: ActiveLocalizedString;
  };
  inPerson: {
    enabled: boolean;
    displayDeadline: boolean;
    copy: LocalizedRichTextObj | undefined;
    inPersonUrl: LocalizedString | undefined;
    inPersonButtonText: LocalizedString | undefined;
    inPersonHeading: ActiveLocalizedString;
  };
};

export type JurisdictionVepConfig = {
  jurisdictionCode: string;
  vepEnabled: boolean;
  locationLookup: {
    displayOnVep: boolean;
    displayInDatesDeadlines: boolean;
  };
  importantDatesAndDeadlines: ImportantDates | null;
  idRequirements: {
    copy: ActiveLocalizedRichTextObj;
  } | null;
  registrationRequirements: {
    copy: ActiveLocalizedRichTextObj;
  } | null;
  howToCompleteBallot: {
    copy: ActiveLocalizedRichTextObj;
  } | null;
  customSection: {
    heading: ActiveLocalizedString;
    copy: ActiveLocalizedRichTextObj;
  } | null;
};

// fallback/default 'everything is off' vep config
export const FALLBACK_VEP_CONFIG_OBJECT: JurisdictionVepConfig = {
  jurisdictionCode: 'unknown',
  vepEnabled: false,
  locationLookup: {
    displayOnVep: false,
    displayInDatesDeadlines: false,
  },
  importantDatesAndDeadlines: null,
  idRequirements: null,
  registrationRequirements: null,
  howToCompleteBallot: null,
  customSection: null,
};

// This previously included the now-deleted “contact capture” experience.
export type BallotRequestExperienceTypes =
  | TypeBallotRequestConfigFields['ballotRequestExperience']['values']
  | 'none';

export type JurisdictionBallotRequestConfig = {
  hasUniversalVBM: boolean;
  ballotRequestFlowEnabled: boolean;
  ballotRequestExperience: BallotRequestExperienceTypes;
  sosBallotRequestUrl: ActiveLocalizedString;
  ballotRequestButtonText: MinimumEnLocalizedString | undefined;
};

export type JurisdictionHotlineFooterOverride = {
  hotlineOverrideCopy: {
    copy: ActiveLocalizedRichTextObj;
  } | null;
  footerOverrideCopy: {
    copy: ActiveLocalizedRichTextObj;
  } | null;
  stateCode: string;
};

export type JurisdictionCustomLandingPageButtons = {
  customButtonAUrl: MinimumEnLocalizedString | undefined;
  customButtonAText: MinimumEnLocalizedString | undefined;
  customButtonBUrl: MinimumEnLocalizedString | undefined;
  customButtonBText: MinimumEnLocalizedString | undefined;
};

export type JurisdictionCustomPage = {
  slug: string;
  title: ActiveLocalizedString;
  content: ActiveLocalizedRichTextObj;
};

// BEGIN JURISDICTION: BOOLEAN TYPES
export type NationalBooleanConfig = {
  [key in State]: JurisdictionBooleanConfig;
};

export type NationalLocalesConfig = {
  [key in State]: JurisdictionLocalesConfig;
};

export type LandingPageBooleans = {
  vepEnabled: boolean;
  displayVotingLocationLookup: boolean;
  hasActiveElection: boolean;
  displayRegistrationLookup: boolean;
  ballotRequestFlowEnabled: boolean;
  hasActiveAlerts: boolean;
};

export type VepBooleans = {
  displayDatesDeadlines: boolean;
  earlyVotingStartIsSameStatewide: boolean;
  includeLocateOnVep: boolean;
  includeLocateInVepDates: boolean;
  displayIdCopy: boolean;
  displayRegistrationCopy: boolean;
  displayHowToCompleteBallotCopy: boolean;
  enableCustomSection: boolean;
};

export type LocationFinderAudit = {
  displayVotingLocationLookup: boolean;
  displayEarlyAbsenteeExcuse: boolean;
  isEarlyVotingAllowed: boolean;
  informationalNotes: {
    dropoffLocations: boolean;
    earlyVoteLocations: boolean;
    electionDayLocations: boolean;
  };
  hasActiveElection: boolean;
  hasLocateOverrideMessage: boolean;
  lookupExperience: LocationLookupExperienceTypes;
  sosLookupUrl: LocalizedString | null;
};

export type BallotRequestBooleans = {
  hasUniversalVbm: boolean;
  ballotRequestFlowEnabled: boolean;
};

export type RegistrationBooleans = {
  registrationFlowEnabled: boolean;
  onlineRegistrationEnabled: boolean;
  onlineRegistrationDeadlineIsDisplayed: boolean;
  mailRegistrationEnabled: boolean;
  mailRegistrationDeadlineIsDisplayed: boolean;
  showRegistrationSteps: boolean;
  inPersonRegistrationEnabled: boolean;
  inPersonRegistrationDeadlineIsDisplayed: boolean;
  sameDayRegistrationEnabled: boolean;
};

export type JurisdictionBooleanConfig = {
  landingPage: LandingPageBooleans;
  vep: VepBooleans;
  locate: LocationFinderAudit;
  ballotRequest: BallotRequestBooleans;
  register: RegistrationBooleans;
};

export type JurisdictionLocalesConfig = {
  supportedLocales: JurisdictionLocaleSupport;
  priorityLocales: JurisdictionLocaleSupport;
};
//END JURISDICTION: BOOLEAN TYPES

export type LinkedAlertFields =
  TypeCustomAlertWithAllLocalesResponse<ActiveLocale>['fields'];

export type ActiveElectionTypes =
  TypeActiveElectionFields['electionType']['values'];
